"use client";

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { signalRManager } from "../lib/api/signalRManager";
import LocalStorage from "../lib/api/localStorage";
import { WebSocketMessage } from "../types/WebSocketMessage";
import {
  setConnecting,
  setConnected,
  setDisconnected,
  setFailed,
  receiveMessage,
  addSubscribedChannel,
  removeSubscribedChannel,
} from "../store/signalRSlice";

interface SignalRContextType {
  isConnected: boolean;
  connectionState: string;
  subscribeToChannel: (channel: string) => void;
  unsubscribeFromChannel: (channel: string) => void;
  sendMessage: (message: WebSocketMessage) => Promise<void>;
  subscribedChannels: Set<string>;
}

const SignalRContext = createContext<SignalRContextType | null>(null);

interface SignalRProviderProps {
  children: React.ReactNode;
}

/**
 * Global SignalR Provider
 * Establishes a single SignalR connection for the entire application
 * Manages dynamic subscriptions for different channels/locations
 */
export const SignalRGlobalProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState("Disconnected");
  const subscribedChannels = useRef(new Set<string>());
  const [, forceUpdate] = useState({});
  const connectionEstablished = useRef(false);
  const isInitializing = useRef(false);

  const triggerUpdate = () => forceUpdate({});

  useEffect(() => {
    const initializeConnection = async () => {
      // Prevent multiple initializations
      if (connectionEstablished.current || isInitializing.current) {
        console.log("SignalR connection already established or initializing, skipping");
        return;
      }

      isInitializing.current = true;
      dispatch(setConnecting());

      // Get JWT token
      const jwtToken = LocalStorage.readString("authToken");

      if (!jwtToken) {
        console.log("Cannot connect to SignalR: missing token");
        isInitializing.current = false;
        return;
      }

      try {
        console.log("Initializing global SignalR connection...");
        connectionEstablished.current = true;
        isInitializing.current = false;

        // Initial message for global channel subscription
        const initialMessage: WebSocketMessage = {
          channel: "GlobalDataSync",
          action: "subscribe",
          method: null,
          fields: null,
          rows: null,
          success: null,
          error: null,
        };

        await signalRManager.connect(
          jwtToken,
          initialMessage,
          (messageData) => {
            // Process received messages
            console.log("Global SignalR message received:", messageData);
            dispatch(receiveMessage(messageData));

            // Dispatch custom event for individual components
            if (typeof window !== "undefined") {
              const event = new CustomEvent("signalr-message", {
                detail: { messageData },
              });
              window.dispatchEvent(event);
            }
          },
          (error) => {
            console.error("SignalR global error:", error);
            setIsConnected(false);
            setConnectionState("Error");
            dispatch(setFailed(error));
          },
          (reason) => {
            console.log("SignalR global connection closed:", reason);
            setIsConnected(false);
            setConnectionState("Disconnected");
            dispatch(setDisconnected(reason));
          },
          () => {
            console.log("SignalR global connection established");
            setIsConnected(true);
            setConnectionState("Connected");
            dispatch(setConnected());
          }
        );
      } catch (error) {
        console.error("Failed to initialize global SignalR:", error);
        connectionEstablished.current = false;
        isInitializing.current = false;
        setIsConnected(false);
        setConnectionState("Error");
        dispatch(setFailed(error instanceof Error ? error.message : "Connection failed"));
      }
    };

    initializeConnection();

    // Cleanup - don't reset connectionEstablished to avoid unnecessary reconnections
    return () => {
      console.log("SignalRGlobalProvider unmounting (keeping connection alive)");
    };
  }, [dispatch]);

  // Connection state monitor
  useEffect(() => {
    const interval = setInterval(() => {
      if (signalRManager.isConnected()) {
        const state = signalRManager.getConnectionState();
        setConnectionState(state);
        setIsConnected(state === "Connected");
      } else {
        setIsConnected(false);
        setConnectionState("Disconnected");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const subscribeToChannel = (channel: string) => {
    console.log(`subscribeToChannel called for: ${channel}`);

    if (!subscribedChannels.current.has(channel)) {
      console.log(`Subscribing to NEW channel: ${channel}`);
      subscribedChannels.current.add(channel);
      signalRManager.addLocationFilter(channel);
      dispatch(addSubscribedChannel(channel));
      triggerUpdate();
      console.log(`Channel ${channel} added to subscriptions`);
    } else {
      console.log(`Already subscribed to channel: ${channel}`);
    }
  };

  const unsubscribeFromChannel = (channel: string) => {
    if (subscribedChannels.current.has(channel)) {
      console.log(`Unsubscribing from channel: ${channel}`);
      subscribedChannels.current.delete(channel);
      signalRManager.removeLocationFilter(channel);
      dispatch(removeSubscribedChannel(channel));
      triggerUpdate();
    }
  };

  const sendMessage = async (message: WebSocketMessage) => {
    if (isConnected) {
      await signalRManager.sendMessage(message);
    } else {
      console.warn("Cannot send message: SignalR not connected");
    }
  };

  const contextValue: SignalRContextType = {
    isConnected,
    connectionState,
    subscribeToChannel,
    unsubscribeFromChannel,
    sendMessage,
    subscribedChannels: subscribedChannels.current,
  };

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
};

/**
 * Hook to use the global SignalR context
 */
export const useSignalR = (): SignalRContextType => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error("useSignalR must be used within a SignalRGlobalProvider");
  }
  return context;
};

/**
 * Compatible WebSocket Provider
 * Maintains the original interface but uses global SignalR
 */
interface CompatibleWebSocketProviderProps {
  channel: string;
}

export const WebSocketProvider: React.FC<CompatibleWebSocketProviderProps> = ({ channel }) => {
  const { subscribeToChannel, isConnected } = useSignalR();

  useEffect(() => {
    console.log(`WebSocketProvider: Effect triggered for channel ${channel}`, {
      isConnected,
      channel,
    });

    if (isConnected && channel) {
      subscribeToChannel(channel);
      console.log(`WebSocketProvider: Successfully subscribed to ${channel} via global SignalR`);
    }
  }, [channel, isConnected, subscribeToChannel]);

  // Compatible component renders nothing - only manages subscription
  return null;
};

export default SignalRGlobalProvider;
